import { HttpService, HttpServiceResult } from '@/http-services/http.service';
import { UtilityTypeIds } from '@ncss/models';
import { AxiosError } from 'axios';
import { MobileUnit, PropertyEquipmentStats, UpdateLeakSensor, UpdateMeter, UpdateUnitLocation } from 'common';

export class UnitService extends HttpService {
  equipmentStats(propertyId: string) {
    return this.send<PropertyEquipmentStats>({
      url: `/Properties/${propertyId}/EquipmentStats`,
      method: 'get',
    });
  }

  findForProperty(propertyId: string): Promise<MobileUnit[]> {
    return this.send<MobileUnit[]>({
      url: `/Properties/${propertyId}/Units`,
      method: 'get',
    }).then((docs) => {
      return docs.map((d) => {
        return new MobileUnit(d);
      });
    });
  }

  findById(id: string) {
    return this.send<MobileUnit>({
      url: `/Units/${id}`,
      method: 'get',
    }).then((d) => new MobileUnit(d));
  }

  async deleteById(id: string): Promise<HttpServiceResult<string>> {
    try {
      await this.send({
        url: `/Units/${id}`,
        method: 'delete',
      });
      return {
        success: true,
        data: id,
      };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to delete unit',
      };
    }
  }

  async updateUnitLocation(unitId: string, data: UpdateUnitLocation): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Location`,
        method: 'put',
        data,
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to update unit location',
      };
    }
  }

  async createUnits(propertyId: string, names: string[], building: string): Promise<HttpServiceResult<MobileUnit[]>> {
    try {
      const docs = await this.send<MobileUnit[]>({
        url: `/Properties/${propertyId}/Units`,
        method: 'post',
        data: { names, building },
      });
      return { success: true, data: docs.map((u) => new MobileUnit(u)) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to create unit',
      };
    }
  }

  async addLeakSensor(unitId: string, location: string): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/LeakSensors`,
        method: 'post',
        data: { location },
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to add Leak Sensor',
      };
    }
  }

  async removeLeakSensor(unitId: string, location: string): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/LeakSensors/${location}`,
        method: 'delete',
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to remove Leak Sensor',
      };
    }
  }

  async programLeakSensor(unitId: string, location: string, deviceId: number): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/LeakSensors/${location}/Device`,
        method: 'post',
        data: { deviceId },
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to program Leak Sensor',
      };
    }
  }

  async unprogramLeakSensor(unitId: string, location: string): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/LeakSensors/${location}/Device`,
        method: 'delete',
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error
            ? e.response.data.error
            : 'Failed to unprogram Leak Sensor',
      };
    }
  }

  async updateLeakSensor(
    unitId: string,
    location: string,
    update: UpdateLeakSensor
  ): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/LeakSensors/${location}`,
        method: 'put',
        data: update,
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to update Leak Sensor',
      };
    }
  }

  async switchLeakSensorLocation(
    unitId: string,
    oldLocation: string,
    newLocation: string
  ): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/LeakSensors/${oldLocation}/Location`,
        method: 'put',
        data: { location: newLocation },
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to update Leak Sensor',
      };
    }
  }

  async switchMeterUtility(
    unitId: string,
    oldUtilityTypeId: UtilityTypeIds,
    newUtilityTypeId: UtilityTypeIds
  ): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Meters/${oldUtilityTypeId}/UtilityType`,
        method: 'put',
        data: { utilityTypeId: newUtilityTypeId },
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to update meter',
      };
    }
  }

  async addMeter(unitId: string, utilityTypeId: UtilityTypeIds): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Meters`,
        data: { utilityTypeId },
        method: 'post',
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to add meter',
      };
    }
  }

  async updateMeter(
    unitId: string,
    utilityTypeId: UtilityTypeIds,
    update: UpdateMeter
  ): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Meters/${utilityTypeId}`,
        method: 'put',
        data: update,
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to add meter',
      };
    }
  }

  async removeMeter(unitId: string, utilityTypeId: UtilityTypeIds): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Meters/${utilityTypeId}`,
        method: 'delete',
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to remove meter',
      };
    }
  }

  async programMeterDevice(
    unitId: string,
    utilityTypeId: UtilityTypeIds,
    deviceId: number
  ): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Meters/${utilityTypeId}/Device`,
        method: 'post',
        data: { deviceId },
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to program device',
      };
    }
  }

  async removeMeterDevice(unitId: string, utilityTypeId: UtilityTypeIds): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Meters/${utilityTypeId}/Device`,
        method: 'delete',
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to remove device',
      };
    }
  }

  async dismissAlert(unitId: string, alertId: string): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Alerts/${alertId}`,
        method: 'delete',
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to dismiss alert',
      };
    }
  }

  async addComment(unitId: string, text: string): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Comments`,
        method: 'post',
        data: { text },
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to add comment',
      };
    }
  }

  async removeComment(unitId: string, commentId: string): Promise<HttpServiceResult<MobileUnit>> {
    try {
      const u = await this.send<MobileUnit>({
        url: `/Units/${unitId}/Comments/${commentId}`,
        method: 'delete',
      });
      return { success: true, data: new MobileUnit(u) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to remove comment',
      };
    }
  }
}
